<script lang="ts">
	import { getVariantName } from '$lib/util/ecom/util';
	import Dropdown from '$lib/components/inputs/Dropdown.svelte';
	import {
		convertSchemaToHtml,
		formatPrice,
		formatPriceRange,
		formatWeight
	} from '$lib/util/ecom/format';
	import { page } from '$app/stores';
	import Section from '$lib/components/layout/Section.svelte';
	import Button from '$lib/components/util/Button.svelte';
	import Link from '$lib/components/navigation/Link.svelte';
	import QuantitySelector from '$lib/components/ecom/QuantitySelector.svelte';
	import { onMount } from 'svelte';
	import Slider from '$lib/components/util/Slider.svelte';
	import type Swiper from 'swiper';
	import SectionSwitcher from '$lib/components/layout/SectionSwitcher.svelte';
	import GlobalMedia from '$lib/components/media/GlobalMedia.svelte';
	import { addToCart, quickCheckout } from '$lib/stores/shopStore';
	import { getText } from '$lib/util/translations';
	import Accordion from '$lib/components/util/Accordion.svelte';
	import { findMachineTypeByHandle, findTastingNoteByHandle } from '$lib/util/ecom/metaobjects';
	import RatingCircles from './RatingCircles.svelte';
	import CollapsibleItem from '../util/CollapsibleItem.svelte';
	import Text from '../Text.svelte';
	import Media from '../media/Media.svelte';
	import ZoomableMedia from '../media/ZoomableMedia.svelte';
	import { injectDiscounts } from '$lib/util/ecom/discounts';

	export let data;

	$: product = data.product;
	$: additionalData = data.additionalData;

	$: variants = product?.variants?.edges;
	$: options = product?.options;

	$: defaultSelectedVariant = variants?.[0]?.node;
	let selectedOptions: Record<string, string> = {};

	let sellingPlans: any[] = [];
	let subscriptionVariantIdx: number = -1;
	let selectedSellingPlanIdx: number = -1;
	$: {
		for (const group of product?.sellingPlanGroups?.edges ?? []) {
			for (const plan of group?.node?.sellingPlans?.edges ?? []) {
				sellingPlans.push(plan.node);
			}
		}
	}

	function previewImage() {
		if (!variants[subscriptionVariantIdx]?.image?.url) {
			return product && product.media && product.media.edges.length > 0
				? product.media.edges[0].node.previewImage.url
				: '';
		}

		return variants[subscriptionVariantIdx]?.image?.url;
	}

	function addSubscription() {
		if (subscriptionVariantIdx >= 0 && selectedSellingPlanIdx >= 0) {
			const variantId = variants[subscriptionVariantIdx]?.node?.id;

			addToCart(
				variantId,
				selectedQuantity,
				price?.amount,
				price?.currencyCode,
				product.title,
				previewImage(),
				sellingPlans[selectedSellingPlanIdx].id
			);
		}
	}

	onMount(() => {
		selectedOptions =
			defaultSelectedVariant?.selectedOptions?.reduce((acc, option) => {
				if (!option.name || !option.value) return acc;
				acc[option.name] = option.value;
				return acc;
			}, {} as Record<string, string>) ?? {};

		try {
			fbq('track', 'ViewContent');
		} catch (err) {}

		try {
			setTimeout(() => {
				window.gtag('event', 'view_item', {
					currency: price?.currencyCode,
					value: price?.amount,
					items: [
						{
							item_id: product?.id,
							item_name: product?.title,
							price: price?.amount,
							quantity: 1
						}
					]
				});
			}, 2000);
		} catch (err) {
			console.error('gta error:', err);
		}
	});

	$: selectedVariant =
		variants?.find((variant) => {
			return variant.node?.selectedOptions?.every((selectedOption) => {
				return selectedOption.name && selectedOptions[selectedOption.name] === selectedOption.value;
			});
		})?.node ?? defaultSelectedVariant;

	$: selectableOptions = options?.filter((item) => item.values && item.values.length > 0) ?? []; // only options with more than one value

	$: comparePrice = selectedVariant?.compareAtPriceV2;
	$: price = selectedVariant?.priceV2;
	// $: unitPrice = selectedVariant?.unitPrice;

	$: quantityAvailable = selectedVariant?.quantityAvailable;

	type Media = {
		url: string;
		altText: string;
	};

	$: media = [
		...(product.media?.edges?.flatMap((image) => image.node) ?? [])
		// ...(product.variants?.edges?.flatMap((variant) => variant.node) ?? [])
	] as Media[];

	let uniqueMedia: any[] = [];
	$: {
		const mediaWithoutVariantImages = media.filter(
			// remover variant images
			(media) =>
				!product.variants?.edges?.find((item) => item?.node?.image?.url === media.previewImage.url)
		);

		const selectedVariantMedia = {
			mediaContentType: 'IMAGE',
			previewImage: selectedVariant?.image
		};

		const tempMedia = [selectedVariantMedia, ...mediaWithoutVariantImages];

		uniqueMedia = tempMedia
			.filter(
				(item, idx) =>
					tempMedia.findIndex((i) => i?.previewImage?.url === item?.previewImage?.url) === idx
			)

			.filter(
				(media) => media?.mediaContentType === 'IMAGE' || media?.mediaContentType === 'VIDEO'
			);

		if (uniqueMedia.length <= 2) {
			uniqueMedia = [...uniqueMedia];
		}
	}
	/*media.filter(
			(item, idx) =>
				media.findIndex((i) => i.url === item.url) === idx || item.mediaContentType === 'VIDEO'
		);*/

	let swiperRef: Swiper | undefined;

	$: {
		// slide swiper to the selected variant image
		if (swiperRef && selectedVariant?.image && uniqueMedia.length > 0) {
			let index = uniqueMedia.findIndex((item) => item.url === selectedVariant?.image?.url);
			if (index === -1) {
				index = 0;
			}
			swiperRef?.slideToLoop(index);
		}
	}

	let selectedQuantity = 1;

	$: currentCartItem = {
		handle: product.handle ?? '',
		title: product.title ?? '',
		image: selectedVariant?.image?.url ?? '',
		price: Number(selectedVariant?.priceV2?.amount) ?? 0,
		discountPrice: Number(selectedVariant?.compareAtPriceV2?.amount) ?? 0,
		currency: selectedVariant?.priceV2?.currencyCode ?? '',
		variants: selectedVariant?.selectedOptions?.map((item: any) => item.value ?? '') ?? [],
		amount: selectedQuantity,
		id: selectedVariant?.id ?? '',
		type: product.product_type?.value ?? 'consumer',
		productId: product.id ?? ''
	};

	let accordionItems: any[];
	$: {
		accordionItems = [];
		if (product.descriptionLong?.value) {
			accordionItems.push({
				title: getText($page.data.globalTexts, 'ecom_description').text,
				text: convertSchemaToHtml(product.descriptionLong?.value) ?? ''
			});
		}
		if (product.how_to_use?.value) {
			accordionItems.push({
				title: getText($page.data.globalTexts, 'ecom_how_to_use').text,
				text: convertSchemaToHtml(product.how_to_use?.value) ?? ''
			});
		}
		if (product.ingredients?.value) {
			accordionItems.push({
				title: getText($page.data.globalTexts, 'ecom_ingredients').text,
				text: convertSchemaToHtml(product.ingredients?.value) ?? ''
			});
		}
	}

	let features: string[];
	$: {
		if (product.features) {
			features = JSON.parse(product.features.value);
		}
	}

	let overlayVideoData: any;

	function getVideoData(media) {
		for (const videoData of media.sources ?? []) {
			if (videoData.width <= 720 && videoData.mimeType === 'video/mp4') {
				return videoData;
			}
		}
		return null;
	}

	$: machineTypes =
		product?.machine_types?.references?.edges?.map((item: any) =>
			findMachineTypeByHandle(item.node?.handle)
		) ?? [];

	$: tastingNotes =
		product?.tasting_notes?.references?.edges?.map((item: any) =>
			findTastingNoteByHandle(item.node?.handle)
		) ?? [];

	// const nonCoffeeCollections = ['gifts', 'accessoires', 'tea-sweets'];

	$: isCoffeProduct = !product?.collections?.edges?.some(
		(item) => item.node?.show_filters?.value === 'false'
	);

	const hiddenCollections = ['all', 'recommendations'];

	$: breadcrumbCollections = product.collections?.edges?.filter(
		(item) => !hiddenCollections.includes(item.node?.handle?.toLowerCase())
	);

	$: selectedSellingPlan = sellingPlans[selectedSellingPlanIdx];

	$: subscriptionBasePrice = product?.variants.edges[subscriptionVariantIdx]?.node?.priceV2;
	$: discount =
		Number(selectedSellingPlan?.priceAdjustments?.[0]?.adjustmentValue?.adjustmentPercentage) /
			100 ?? 0;
	$: subscriptionDiscountedPrice = {
		...subscriptionBasePrice,
		amount: Math.round(subscriptionBasePrice?.amount * (1 - discount) * 100) / 100
	};
	$: subscriptionShowPrice = subscriptionVariantIdx >= 0 && selectedSellingPlanIdx >= 0;

	$: benefits = product?.collections?.edges?.some(
		(item: any) => !!item.node?.show_alternative_benefits
	)
		? $page.data.globals.product_detail__benefits_alt
		: $page.data.globals.product_detail__benefits ?? [];
</script>

<Section class="section-product-hero">
	<div class="section-product-hero-wrapper">
		<!-- <div class="breadcrumbs mobile">
			<span class="breadcrumb-item">
				<Link link="shop" type="internal">Shop</Link>
			</span>

			{#if (product.collections?.edges?.length ?? 0) > 0}
				<span class="seperator"><GlobalMedia key="icon-arrow-right" /></span>
			{/if}

			{#each product.collections?.edges ?? [] as item, idx}
				<span class="breadcrumb-item">
					<Link link={'shop/' + item.node?.handle} type="internal">
						{item.node?.title}
					</Link>
				</span>
				{#if idx < (product.collections?.edges?.length ?? 0) - 1}
					<span class="seperator">|</span>
				{/if}
			{/each}
		</div> -->
		<div class="gallery-column column">
			<div class="wrapper mobile">
				{#key uniqueMedia}
					<Slider
						name="product-detail-swiper-{product.handle}"
						navigation={uniqueMedia?.length > 1 ? true : false}
						type="single"
						slidesPerView={1}
						centeredSlides={false}
						pagination={true}
						options={{ loop: true }}
						bind:swiper={swiperRef}
					>
						{#each uniqueMedia ?? [] as media}
							{#key 'media' + media.id}
								{#if media.mediaContentType === 'IMAGE' || media.mediaContentType === 'VIDEO'}
									<div class="swiper-slide">
										{#if media.mediaContentType === 'IMAGE'}
											<ZoomableMedia>
												<img src={media.previewImage.url} alt={media.previewImage.altText ?? ''} />
											</ZoomableMedia>
										{/if}
										{#if media.mediaContentType === 'VIDEO' && getVideoData(media)}
											<div class="video-container">
												<video muted loop playsinline autoplay>
													<source
														type={getVideoData(media).mimeType}
														src={getVideoData(media).url}
													/>
												</video>
											</div>
										{/if}
									</div>
								{/if}
							{/key}
						{/each}
					</Slider>
				{/key}
			</div>
			<div class="wrapper desktop">
				<div class="gallery-grid">
					{#each uniqueMedia ?? [] as media}
						{#key 'media' + media.id}
							{#if media.mediaContentType === 'IMAGE' || media.mediaContentType === 'VIDEO'}
								<div class="media-wrapper">
									{#if media.mediaContentType === 'IMAGE'}
										<ZoomableMedia>
											<img src={media.previewImage.url} alt={media.previewImage.altText ?? ''} />
										</ZoomableMedia>
									{/if}
									{#if media.mediaContentType === 'VIDEO' && getVideoData(media)}
										<div class="video-container">
											<video muted loop playsinline autoplay>
												<source type={getVideoData(media).mimeType} src={getVideoData(media).url} />
											</video>
										</div>
									{/if}
								</div>
							{/if}
						{/key}
					{/each}
				</div>
			</div>
		</div>
		<div class="content-column column">
			<div class="breadcrumbs desktop">
				<span class="breadcrumb-item">
					<Link link="shop" type="internal">Shop</Link>
				</span>

				{#if (breadcrumbCollections?.length ?? 0) > 0}
					<span class="seperator"><GlobalMedia key="icon-caret-right" /></span>
				{/if}

				{#each breadcrumbCollections ?? [] as item, idx}
					<span class="breadcrumb-item">
						<Link link={'shop/' + item.node?.handle} type="internal">
							{item.node?.title}
						</Link>
					</span>
					{#if idx < (breadcrumbCollections?.length ?? 0) - 1}
						<span class="seperator">|</span>
					{/if}
				{/each}
			</div>
			<div class="content">
				<div class="title-grid">
					<h1 class="title">{product.title}</h1>
					{#if product.subtitle_1 || product.subtitle_2}
						<div class="info-row">
							{#if product.subtitle_1}
								<div class="type-bean">{product.subtitle_1.value ?? ''}</div>
							{/if}
							{#if product.subtitle_1 && product.subtitle_2}
								<div class="seperator" />
							{/if}
							{#if product.subtitle_2}
								<div class="type-blend">{product.subtitle_2.value ?? ''}</div>
							{/if}
						</div>
					{/if}
					<div class="pricing">
						<div class="price-wrapper">
							{#if comparePrice}
								<div class="compare-price">
									{formatPrice(comparePrice?.amount, comparePrice?.currencyCode, $page.params.lang)}
								</div>
							{/if}

							{#if !selectedVariant}
								<div class="price-range">
									{formatPriceRange(
										injectDiscounts(product.priceRange?.minVariantPrice?.amount),
										injectDiscounts(product.priceRange?.maxVariantPrice?.amount),
										product.priceRange?.minVariantPrice?.currencyCode ?? '',
										$page.params.lang
									)}
								</div>
							{:else}
								<div class="price">
									{formatPrice(
										injectDiscounts(price?.amount, product?.id),
										price?.currencyCode,
										$page.params.lang
									)}
								</div>
							{/if}
						</div>
						<div class="tax-notice"><Text key="ecom_vat_text_product_page" /></div>
						<!-- TODO: make dynamic -->
					</div>
				</div>

				<!-- <div class="description">{@html product.descriptionHtml}</div> -->
				<div class="tool-row">
					{#if selectableOptions.length > 0}
						<div class="option-selectors">
							{#each selectableOptions ?? [] as option}
								<div class="option-selector">
									<Dropdown bind:value={selectedOptions[option.name ?? '']}>
										<option value="" hidden={true}>{option.name}</option>
										{#each option.values ?? [] as value}
											<option {value}>{value}</option>
										{/each}
									</Dropdown>
								</div>
							{/each}
						</div>
					{/if}

					<div class="quanitity-selector">
						<QuantitySelector bind:value={selectedQuantity} max={quantityAvailable} />
					</div>
				</div>

				<div class="buttons">
					{#if selectedVariant.availableForSale}
						<!-- <div class="quickbuy">
							<Button
								textKey="ecom_quick_buy"
								variant="outline"
								style="yellow"
								on:click={() => quickCheckout(currentCartItem)}
							/>
						</div> -->
						<div class="add-to-cart">
							<Button
								textKey="ecom_add_to_cart"
								variant="full"
								size="xxl"
								style="yellow"
								on:click={() =>
									addToCart(
										selectedVariant?.id,
										selectedQuantity,
										price?.amount,
										price?.currencyCode,
										product.title,
										previewImage()
									)}
							/>
						</div>
					{:else}
						<div class="add-to-cart">
							<Button
								textKey="ecom_sold_out"
								variant="full"
								size="xxl"
								style="yellow"
								disabled={true}
							/>
						</div>
					{/if}
				</div>

				<div class="details">
					<CollapsibleItem labelKey="ecom_product_detail__benefits">
						<div class="text-with-icon-wrapper">
							{#each benefits as { element__text_and_icon_id: item }}
								<div class="text-with-icon">
									<div class="icon"><Media {...item.media} /></div>
									<div class="text text-medium">{item.text}</div>
								</div>
							{/each}
						</div>
					</CollapsibleItem>

					{#if isCoffeProduct}
						<CollapsibleItem labelKey="ecom_product_detail__flavor">
							<div class="flavor-grid">
								<div class="flavor-item">
									<div class="label">
										<Text key="ecom_product_detail__roast_level" />
									</div>
									<div class="content">
										<RatingCircles
											rating={product?.roast_level?.value}
											labelKeyPrefix="roast_level"
										/>
									</div>
								</div>

								<div class="flavor-item">
									<div class="label">
										<Text key="ecom_product_detail__caffeine_level" />
									</div>
									<div class="content">
										<RatingCircles
											rating={product?.caffeine_level?.value}
											labelKeyPrefix="caffeine_level"
										/>
									</div>
								</div>

								<div class="flavor-item">
									<div class="label">
										<Text key="ecom_product_detail__acidity_level" />
									</div>
									<div class="content">
										<RatingCircles
											rating={product?.acidity_level?.value}
											labelKeyPrefix="acidity_level"
										/>
									</div>
								</div>

								<div class="divider" />

								<div class="flavor-item">
									<div class="label">
										<Text key="ecom_product_detail__variety" />
									</div>
									<div class="content">
										{product?.variety?.value ?? '-'}
									</div>
								</div>

								<div class="flavor-item">
									<div class="label">
										<Text key="ecom_product_detail__country" />
									</div>
									<div class="content">
										{product?.country?.value ?? '-'}
									</div>
								</div>

								<div class="flavor-item">
									<div class="label">
										<Text key="ecom_product_detail__processing" />
									</div>
									<div class="content">
										{product?.processing?.value ?? '-'}
									</div>
								</div>

								<div class="divider" />

								<div class="flavor-item tasting-notes">
									<div class="label">
										<Text key="ecom_product_detail__tasting_notes" />
									</div>
									<div class="content">
										<div class="text-with-icon-wrapper inline">
											{#each tastingNotes as item}
												<div class="text-with-icon">
													<div class="icon">
														<img
															src={item?.icon?.reference?.image?.url}
															alt={item?.label?.value ?? ''}
														/>
													</div>
													<div class="text">
														{item?.label?.value}
													</div>
												</div>
											{/each}
										</div>
									</div>
								</div>
							</div>
						</CollapsibleItem>
					{/if}

					{#if isCoffeProduct}
						<CollapsibleItem labelKey="ecom_product_detail__machines">
							<div class="text-with-icon-wrapper inline">
								{#each machineTypes as item}
									<div class="text-with-icon">
										<div class="icon">
											<img src={item?.icon?.reference?.image?.url} alt={item?.label?.value ?? ''} />
										</div>
										<div class="text">
											{item?.label?.value}
										</div>
									</div>
								{/each}
							</div>
						</CollapsibleItem>
					{/if}

					<CollapsibleItem labelKey="ecom_product_detail__description">
						<div class="description">
							{@html product.descriptionHtml}
						</div>
					</CollapsibleItem>

					<div class="buttons">
						{#if selectedVariant.availableForSale}
							<!-- <div class="quickbuy">
								<Button
									textKey="ecom_quick_buy"
									variant="outline"
									style="yellow"
									on:click={() => quickCheckout(currentCartItem)}
								/>
							</div> -->
							<div class="add-to-cart">
								<Button
									textKey="ecom_add_to_cart"
									variant="full"
									size="xxl"
									style="yellow"
									on:click={() =>
										addToCart(
											selectedVariant?.id,
											selectedQuantity,
											price?.amount,
											price?.currencyCode,
											product.title,
											previewImage()
										)}
								/>
							</div>
						{:else}
							<div class="add-to-cart">
								<Button
									textKey="ecom_sold_out"
									variant="full"
									size="xxl"
									style="yellow"
									disabled={true}
								/>
							</div>
						{/if}
					</div>

					{#if sellingPlans?.length > 0}
						<div id="subscription" />
						<CollapsibleItem labelKey="ecom_product_detail__subscribe">
							<div class="text-with-icon-wrapper">
								{#each $page.data.globals.subscription__benefits as { element__text_and_icon_id: item }}
									<div class="text-with-icon">
										<div class="icon"><Media {...item.media} /></div>
										<div class="text text-medium">{item.text}</div>
									</div>
								{/each}
							</div>
							<div class="subscription-wrapper">
								<div class="subscription-selection">
									<div class="option first">
										<span>1.<Text key="package_size" /></span>
										<Dropdown bind:value={subscriptionVariantIdx} type="small">
											<option selected value={-1} disabled
												>{getText($page.data.globalTexts, 'ecom_which_size').text}</option
											>
											{#each variants as variant, idx}
												<option value={idx}>{getVariantName(variant.node)}</option>
											{/each}
										</Dropdown>
									</div>
									<div class="option">
										<span>2.<Text key="amount_packs" /></span>
										<div class="quanitity-selector">
											<QuantitySelector bind:value={selectedQuantity} max={10} />
										</div>
									</div>
									<div class="option">
										<span>3.<Text key="interval" /></span>
										<Dropdown bind:value={selectedSellingPlanIdx} type="small">
											<option selected value={-1} disabled
												>{getText($page.data.globalTexts, 'ecom_which_interval').text}</option
											>
											{#each sellingPlans as sellingPlan, idx}
												<option value={idx}>
													{sellingPlan?.name}
													<!-- {#if sellingPlan.deliveryPolicy}
														{sellingPlan.deliveryPolicy?.intervalCount ?? ''}
														{getText(
															$page.data.globalTexts,
															'ecom_' +
																sellingPlan.deliveryPolicy?.interval.toLowerCase() +
																(sellingPlan.deliveryPolicy?.intervalCount > 1 ? 's' : '')
														).text}
													{/if} -->
												</option>
											{/each}
										</Dropdown>
									</div>
									<div class="option">
										{#if subscriptionShowPrice}
											<span><Text key="ecom_product_detail__subscribe__price__title" /></span>
											<div class="price-wrapper">
												<div class="price">
													{formatPrice(
														injectDiscounts(subscriptionDiscountedPrice?.amount * selectedQuantity),
														subscriptionDiscountedPrice?.currencyCode,
														$page.params.lang
													)}
												</div>
												<div class="compare-price">
													{formatPrice(
														injectDiscounts(subscriptionBasePrice?.amount * selectedQuantity),
														subscriptionBasePrice?.currencyCode,
														$page.params.lang
													)}
												</div>
											</div>
										{/if}
									</div>
								</div>
								<div class="subscribe-button">
									<Button textKey="subscribe" on:click={addSubscription} style="yellow" />
								</div>
							</div>
						</CollapsibleItem>
					{/if}
					<!-- TODO: add subscription logic -->
				</div>
			</div>
		</div>
	</div></Section
>

<SectionSwitcher sections={additionalData?.sections} />
<SectionSwitcher sections={data.globalProductSections?.sections} />

<style lang="scss">
	.section-product-hero-wrapper {
		display: grid;
		grid-template-columns: 5fr 6fr;

		@include min-media(tablet) {
			margin-top: v(content-gap-xl);
			gap: v(content-gap-4xl);
		}

		@include media(tablet) {
			grid-template-columns: 1fr;
			gap: v(content-gap-xs);
		}
	}

	.subscription-wrapper {
		.price-wrapper {
			display: flex;
			flex-direction: row;
			justify-content: flex-end;
			align-items: flex-end;
			text-wrap: nowrap;
			flex-wrap: wrap;
			gap: v(content-gap-xs);
			height: v(inpute__height);
			padding-top: 10px;

			.price {
				@include font(h4);
			}
			.compare-price {
				@include font(text);
				text-decoration: line-through;
				line-height: 100%;
			}
		}
	}

	.gallery-column {
		width: 100%;
		overflow: hidden;
		position: relative;

		@include min-media(tablet) {
			padding-bottom: 2rem;
		}

		@include media(tablet) {
			margin-inline: calc(-1 * v(page-padding));
			width: calc(100% + 2 * v(page-padding));
		}

		:global(.swiper) {
			width: 100%;
		}

		:global(img) {
			width: 100%;
			aspect-ratio: 1;
			object-fit: contain;
			object-position: center;
		}

		.wrapper {
			position: relative;

			&.mobile {
				@include min-media(tablet) {
					display: none;
				}
			}
			&.desktop {
				@include media(tablet) {
					display: none;
				}
			}
		}

		:global(.navigation) {
			position: absolute;
			top: 50%;
			inset-inline-start: 0;
			width: 100%;
			justify-content: space-between;
			transform: translateY(-50%);
			z-index: 2;
			padding: 0 v(content-padding-sm);
		}

		:global(.swiper-pagination) {
			bottom: 16px !important;
		}

		:global(video) {
			width: 100%;
			height: 100%;
			object-fit: cover;
		}
	}

	.gallery-grid {
		display: grid;
		grid-template-columns: 1fr 1fr;
		gap: v(content-gap-sm);

		.media-wrapper {
			width: 100%;
			height: 100%;

			img,
			video {
				width: 100%;
				aspect-ratio: 1;
				object-fit: cover;
				object-position: center;
			}

			&:nth-child(3n + 1) {
				grid-column: 1 / span 2;
			}
		}
	}

	.breadcrumbs {
		color: color(grey_05);
		display: flex;

		&.mobile {
			@include min-media(tablet) {
				display: none;
			}
		}

		&.desktop {
			@include media(tablet) {
				display: none;
			}
		}

		@include font(text-sm);
		.breadcrumb-item {
			color: inherit;
			text-decoration: none;

			:global(a) {
				color: inherit;
				text-decoration: none;
			}

			cursor: pointer;
			@include transition(all);

			&:hover {
				opacity: 0.5;
			}
		}

		.seperator {
			padding-inline: v(content-padding-sm);

			:global(svg) {
				height: 0.75em;
				width: 0.75em;
				:global(path) {
					fill: color(grey_05);
				}
			}
		}
	}

	.content-column {
		display: flex;
		flex-direction: column;
		gap: v(content-gap);

		.content {
			display: flex;
			flex-direction: column;
			gap: v(content-gap-lg);

			@include media(tablet) {
				gap: v(content-gap-sm);
			}
		}
	}

	.title-grid {
		display: flex;
		flex-direction: column;

		& > * {
			border-bottom: 1px solid color(black);
			padding-block: v(content-padding-xs);
		}

		.title {
			@include font(h2);
		}

		.info-row {
			@include font(h5);
			display: flex;

			.seperator {
				border-inline-end: 1px solid color(black);
				margin-inline: v(content-padding-lg);

				@include media(tablet) {
					margin-inline: v(content-padding-sm);
				}
			}

			& > * {
				padding-block: v(content-padding-xs);
			}

			.type-bean {
				// border-inline-end: 1px solid color(black);
			}

			.type-blend {
				flex: 1;
			}
		}
	}

	.pricing {
		padding-block: v(content-padding-sm);
		display: flex;
		flex-direction: column;
		gap: v(content-gap-2xs);

		@include media(tablet) {
			display: flex;
			flex-direction: row;
			align-items: baseline;
			gap: v(content-gap-xs);
			padding-block: v(content-padding-xs);
		}

		.price-wrapper {
			@include font(h3);
			display: flex;
			flex-direction: row;
			gap: v(content-gap);

			.compare-price {
				opacity: 30%;
				text-decoration: line-through;
				text-decoration-thickness: 2px;
			}

			.compare-price,
			.price {
				white-space: nowrap;
			}
		}

		.tax-notice {
			@include font(body-xs);
		}
	}

	.description {
		:global(table) {
			height: auto !important;
			width: 100% !important;
			text-align: left;
			:global(a) {
				color: color(black) !important;
				text-decoration: underline;
				&:hover {
					text-decoration: none;
				}
			}

			:global(th) {
				font-weight: bold;
			}

			:global(tr) {
				padding-block: v(content-gap-xs);
			}

			:global(td),
			:global(th) {
				padding: v(content-gap-2xs) v(content-gap-xs);

				&:first-child {
					padding-inline-start: 0;
				}

				&:last-child {
					padding-inline-end: 0;
				}
			}

			:global(*) {
				height: auto !important;
				width: auto !important;
			}
		}
	}

	.option-selectors {
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		gap: v(content-gap);
	}

	.tool-row {
		display: flex;
		justify-content: flex-start;
		gap: v(content-gap);
		flex-wrap: wrap;

		@include media(tablet) {
			justify-content: space-between;
		}
	}

	.buttons {
		display: flex;
		flex-direction: column;
		gap: v(content-gap);
		white-space: nowrap;

		:global(.button) {
			background-color: red;
			flex: 1;
			width: 100%;
			justify-content: center;
		}
	}

	.details {
		display: flex;
		flex-direction: column;
		gap: v(content-gap-lg);

		container-type: inline-size;

		@include media(tablet) {
			margin-top: v(content-gap);
		}
	}

	.text-with-icon-wrapper {
		display: flex;
		flex-direction: column;
		gap: v(content-gap-xs);

		&.inline {
			flex-direction: row;
			gap: v(content-gap-xl);

			.text-with-icon {
				gap: v(content-gap-xs);
			}
		}
	}

	.text-with-icon {
		display: flex;
		flex-direction: row;
		gap: v(content-gap);
		align-items: center;

		.icon {
			height: 1.8rem;
			aspect-ratio: 1;

			img {
				height: 100%;
				width: 100%;
				object-fit: contain;
			}
		}

		.text {
			@include font(text);
			margin-top: 0.2em;

			&.text-medium {
				@include font(text);
			}
		}
	}

	.flavor-grid {
		display: grid;
		grid-template-columns: repeat(3, 1fr);
		gap: v(content-gap-sm);

		@container (max-width: 500px) {
			grid-template-columns: 1fr 1fr !important;

			.divider {
				display: none;
			}
		}

		.divider {
			grid-column: 1 / span 3;
			border-top: 1px solid color(grey_02);
		}

		.flavor-item {
			display: flex;
			flex-direction: column;
			gap: v(content-gap-2xs);

			.label {
				@include font(h6);
				font-size: 14px;
			}

			.content {
				@include font(text);
			}

			&.tasting-notes {
				grid-column: 1 / span 3;

				@container (max-width: 500px) {
					grid-column: 1 / span 2;
				}
			}

			@container (max-width: 500px) {
				&:nth-child(1),
				&:nth-child(2),
				&:nth-child(3) {
					grid-column: 1;
				}
				&:nth-child(5) {
					grid-column: 2;
					grid-row: 1;
				}
				&:nth-child(6) {
					grid-column: 2;
					grid-row: 2;
				}
				&:nth-child(7) {
					grid-column: 2;
					grid-row: 3;
				}
			}
		}
	}

	.subscription-wrapper {
		display: inline-flex;
		flex-direction: column;
		margin-top: v(content-gap-xl);
		.option {
			:global(.dropdown) {
				width: 11rem !important;
			}

			:global(span) {
				@include font(h6);
				display: flex;
				gap: v(content-gap-xs);
				padding-bottom: v(content-gap-xs);
			}
			:global(.textfield) {
				@include font(text-sm);
			}
			:global(.quantitiy-selector) {
				width: 10rem !important;
				@media (max-width: 600px) {
					width: 12rem !important;
				}
			}
		}
	}

	.subscription-selection {
		display: inline-flex;
		gap: v(content-padding-sm);
		margin: v(content-padding-sm) 0;
		flex-wrap: wrap;

		:global(.dropdown) {
			:global(select) {
				background: color(white) !important;
			}
		}
		@media (max-width: 600px) {
			flex-direction: column;
		}
	}

	.subscribe-button {
		:global(button) {
			width: 11rem !important;
			display: flex;
			justify-content: center;
		}
	}
</style>
